import React, { FC, useMemo } from "react";
import { Flex } from "@evergis/ui";
import { GeometryType } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { TemplatesContainer, TemplatesTitle } from "../../styled";
import { TemplateLabel } from "./TemplateLabel";
import { TemplateForm } from "./TemplateForm/TemplateForm";
import { TemplateType } from "./TemplateForm/TemplateFormActions";
import { useToggleTemplate } from "../../hooks/useToggleTemplate";

import { images } from "../../assets";

type TemplateConfig = {
  type: TemplateType;
  text: string;
  width: string;
  img: string;
};

interface CalculationTemplatesProps {
  layerName: string;
  handleSetTemplateValue: (value: string) => void;
  toggleMode: VoidFunction;
  geometryAttribute: string;
  geometryType: GeometryType;
  available?: boolean;
}

export const CalculationTemplates: FC<CalculationTemplatesProps> = ({
  layerName,
  geometryAttribute,
  geometryType,
  handleSetTemplateValue,
  toggleMode,
  available,
}) => {
  const { t } = useTranslation("dialogs");
  const { templateVisibility, toggleTemplate } = useToggleTemplate();
  const geometryTypeInfo = { geometryAttribute, geometryType };

  const templates: TemplateConfig[] = useMemo(
    () => [
      {
        type: TemplateType.Attribute,
        text: t("featuresMultiEdit.attribute"),
        width: "7.5rem",
        img: images.attr,
      },
      {
        type: TemplateType.AttrValue,
        text: t("featuresMultiEdit.attributeAndValue"),
        width: "8.5rem",
        img: images.attrNum,
      },
      {
        type: TemplateType.AttrAttr,
        text: t("featuresMultiEdit.attributeAndAttribute"),
        width: "7.5rem",
        img: images.attrAttr,
      },
      {
        type: TemplateType.Geometry,
        text: t("featuresMultiEdit.geometryOptions"),
        width: "9.875rem",
        img: images.geometry,
      },
    ],
    [t],
  );

  return (
    <TemplatesContainer>
      {available && (
        <>
          <TemplatesTitle>{t("featuresMultiEdit.orUseTemplates")}</TemplatesTitle>
          <Flex style={{ marginLeft: "2.5rem" }}>
            {templates.map((template: TemplateConfig) => (
              <TemplateLabel
                key={template.type}
                type={template.type}
                img={template.img}
                text={template.text}
                width={template.width}
                isTemplateOpen={templateVisibility[template.type]}
                toggleTemplate={toggleTemplate}
              >
                <TemplateForm
                  templateType={template.type}
                  layerName={layerName}
                  handleSetTemplateValue={handleSetTemplateValue}
                  toggleTemplate={toggleTemplate}
                  toggleMode={toggleMode}
                  geometryTypeInfo={geometryTypeInfo}
                />
              </TemplateLabel>
            ))}
          </Flex>
        </>
      )}
    </TemplatesContainer>
  );
};
